import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Search, CheckCheck } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

// Prefer the stored category; fall back to inferring from content for older items.
function notifType(n) {
  if (n.category && ["placements", "touchpoints", "events", "other"].includes(n.category)) return n.category;
  const t = `${n.subject || ""} ${n.body || ""}`.toLowerCase();
  if (t.includes("event")) return "events";
  if (t.includes("touchpoint") || t.includes("lunch") || t.includes("graduation")) return "touchpoints";
  if (n.placement_request_id || t.includes("placement") || t.includes("facility") || t.includes("paperwork")) return "placements";
  return "other";
}
const TYPE_LABELS = { placements: "Placements", touchpoints: "Touchpoints", events: "Events", other: "Other" };

export default function Notifications() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [urgentOnly, setUrgentOnly] = useState("all");
  const [type, setType] = useState("all");

  const load = () => api.get("/notifications").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const markOne = async (n) => {
    if (n.status === "unread") { await api.post(`/notifications/${n.id}/read`); }
    if (n.placement_request_id) navigate(`/placements/${n.placement_request_id}`);
    else load();
  };
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  const ql = q.trim().toLowerCase();
  const filtered = (rows || []).filter((n) => {
    if (status !== "all" && n.status !== status) return false;
    if (urgentOnly === "urgent" && !n.urgent) return false;
    if (type !== "all" && notifType(n) !== type) return false;
    if (ql && !`${n.subject} ${n.body}`.toLowerCase().includes(ql)) return false;
    return true;
  });
  const unreadCount = (rows || []).filter((n) => n.status === "unread").length;
  const typeCounts = (rows || []).reduce((acc, n) => { const t = notifType(n); acc[t] = (acc[t] || 0) + 1; return acc; }, {});

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Your full notification history">
        {unreadCount > 0 && <Button onClick={markAll} variant="outline" data-testid="notif-mark-all"><CheckCheck className="w-4 h-4 mr-1" />Mark all read ({unreadCount})</Button>}
      </PageHeader>

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notifications…" className="pl-9" data-testid="notif-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="notif-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44" data-testid="notif-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {["placements", "touchpoints", "events", "other"].map((t) => (
                <SelectItem key={t} value={t}>{TYPE_LABELS[t]}{typeCounts[t] ? ` (${typeCounts[t]})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={urgentOnly} onValueChange={setUrgentOnly}>
            <SelectTrigger className="w-40" data-testid="notif-urgent-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="urgent">Urgent only</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-zinc-400 ml-auto" data-testid="notif-count">{filtered.length} of {(rows || []).length}</span>
        </div>

        {!rows ? <EmptyState text="Loading…" /> :
          filtered.length === 0 ? <div data-testid="notif-empty"><EmptyState text="No notifications match your filters." /></div> : (
          <div className="divide-y">
            {filtered.map((n) => (
              <button key={n.id} onClick={() => markOne(n)} data-testid={`notif-${n.id}`}
                className={`w-full text-left flex items-start gap-3 px-2 py-3 hover:bg-zinc-50 transition-colors ${n.status === "unread" ? "bg-blue-50/40" : ""}`}>
                <div className="mt-1 shrink-0">
                  {n.urgent ? <span className="w-2.5 h-2.5 rounded-full bg-cc-orange block" /> : <Bell className="w-4 h-4 text-zinc-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm ${n.status === "unread" ? "font-semibold text-zinc-900" : "text-zinc-700"}`}>{n.subject}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{TYPE_LABELS[notifType(n)]}</span>
                      <span className="text-[11px] text-zinc-400">{fmt(n.sent_at)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">{n.body}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
